import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Copy, Share2, Loader2, ExternalLink, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const PLATFORMS = [
  { key: "twitter", label: "X (Twitter)", color: "bg-neutral-900 text-white" },
  { key: "linkedin", label: "LinkedIn", color: "bg-[#0A66C2] text-white" },
  { key: "instagram", label: "Instagram", color: "bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] text-white" },
  { key: "facebook", label: "Facebook", color: "bg-[#1877F2] text-white" },
  { key: "tiktok", label: "TikTok", color: "bg-black text-white" },
  { key: "reddit", label: "Reddit", color: "bg-[#FF4500] text-white" },
  { key: "youtube", label: "YouTube", color: "bg-[#FF0000] text-white" },
];

const shareUrls = {
  twitter: (t) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}`,
  linkedin: (t) => `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(t)}`,
  facebook: (t) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://grantflow.app")}&quote=${encodeURIComponent(t)}`,
  reddit: (t) => `https://www.reddit.com/submit?title=${encodeURIComponent(t.slice(0, 300))}&text=${encodeURIComponent(t)}`,
};

function AuthedImage({ url, alt = "" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objectUrl;
    (async () => {
      try {
        const r = await api.get(url, { responseType: "blob" });
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      } catch (e) { /* fall through */ }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);
  if (!src) return <div className="w-full aspect-square bg-neutral-100 rounded-lg animate-pulse" />;
  return <img src={src} alt={alt} className="w-full rounded-lg border border-neutral-200" />;
}

export default function SocialStudio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("confident");
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set(["twitter", "linkedin", "instagram"]));
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imgPrompt, setImgPrompt] = useState("");
  const [imgAspect, setImgAspect] = useState("square");
  const [imgLoading, setImgLoading] = useState(false);

  const toggle = (k) => {
    const s = new Set(selectedPlatforms);
    s.has(k) ? s.delete(k) : s.add(k);
    setSelectedPlatforms(s);
  };

  const isPro = user?.subscription_status === "active";

  const generatePosts = async () => {
    if (!isPro) { nav("/pricing"); return; }
    if (!topic.trim() || selectedPlatforms.size === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/api/social/generate-post", {
        topic, tone, platforms: Array.from(selectedPlatforms),
      });
      setPosts(data);
      toast.success("Posts drafted");
    } catch (e) {
      if (e.response?.status === 402) { nav("/pricing"); return; }
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const generateImage = async () => {
    if (!isPro) { nav("/pricing"); return; }
    if (!imgPrompt.trim()) return;
    setImgLoading(true);
    try {
      const { data } = await api.post("/api/social/generate-image", {
        prompt: imgPrompt, aspect: imgAspect,
      });
      setImage(data);
      toast.success("Image generated");
    } catch (e) {
      if (e.response?.status === 402) { nav("/pricing"); return; }
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally { setImgLoading(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="mb-8">
          <div className="overline mb-2">SOCIAL STUDIO</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="social-title">One topic. Seven platforms. Thirty seconds.</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">AI-drafts a native-feeling post for every platform, generates matching imagery with Nano Banana, and gives you 1-click share links.</p>
        </div>

        {!isPro && (
          <div className="mb-8 rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="social-upgrade-banner">
            <div>
              <div className="font-medium">Social Studio is a Pro feature.</div>
              <div className="text-sm text-neutral-600">Upgrade to $100/mo to unlock unlimited post + image generation.</div>
            </div>
            <button onClick={() => nav("/pricing")} className="btn-accent"><Sparkles size={16} /> Upgrade</button>
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: post generator */}
          <div className="lg:col-span-3 gf-card p-6" data-testid="post-generator-panel">
            <h2 className="font-display text-2xl font-semibold mb-4">Draft posts</h2>
            <label className="text-sm font-medium">What are we posting about?</label>
            <textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. 'How nonprofits can win a $250K MacArthur grant with AI-drafted narratives'"
              className="mt-1 w-full border border-neutral-200 rounded-lg p-3" data-testid="social-topic" />

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-1 h-10" data-testid="social-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confident">Confident</SelectItem>
                    <SelectItem value="educational">Educational</SelectItem>
                    <SelectItem value="inspiring">Inspiring</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-sm font-medium mb-2">Platforms</div>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => {
                  const active = selectedPlatforms.has(p.key);
                  return (
                    <button key={p.key} onClick={() => toggle(p.key)}
                      className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${active ? p.color + " border-transparent" : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"}`}
                      data-testid={`platform-${p.key}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button onClick={generatePosts} disabled={loading || !topic.trim() || selectedPlatforms.size === 0}
              className="btn-primary mt-6" data-testid="social-generate-btn">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Drafting…</> : <><Sparkles size={16} /> Draft {selectedPlatforms.size} post{selectedPlatforms.size > 1 ? "s" : ""}</>}
            </button>

            {posts && posts.posts && (
              <div className="mt-8 space-y-4" data-testid="post-results">
                {Object.entries(posts.posts).map(([platform, text]) => {
                  const p = PLATFORMS.find((x) => x.key === platform) || { label: platform, color: "bg-neutral-100" };
                  const share = shareUrls[platform];
                  return (
                    <div key={platform} className="border border-neutral-200 rounded-xl overflow-hidden" data-testid={`post-${platform}`}>
                      <div className={`px-4 py-2 ${p.color} font-medium text-sm flex items-center justify-between`}>
                        <span>{p.label}</span>
                        <Badge variant="secondary" className="bg-white/20 text-white border-transparent">{text.length} chars</Badge>
                      </div>
                      <div className="p-4">
                        <div className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">{text}</div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <button onClick={() => copy(text)} className="btn-ghost text-xs" data-testid={`copy-${platform}`}><Copy size={12} /> Copy</button>
                          {share ? (
                            <a href={share(text)} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid={`share-${platform}`}>
                              <Share2 size={12} /> Post on {p.label}
                            </a>
                          ) : (
                            <span className="text-xs text-neutral-500 self-center">Copy + paste in the {p.label} app</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: image gen */}
          <div className="lg:col-span-2 gf-card p-6" data-testid="image-generator-panel">
            <h2 className="font-display text-2xl font-semibold mb-4">Draft an image</h2>
            <label className="text-sm font-medium">What should the image show?</label>
            <textarea rows={3} value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)}
              placeholder="e.g. 'A confident Black woman entrepreneur working at a modern desk with sunlight coming in'"
              className="mt-1 w-full border border-neutral-200 rounded-lg p-3" data-testid="image-prompt" />
            <div className="mt-4">
              <label className="text-sm font-medium">Aspect</label>
              <Select value={imgAspect} onValueChange={setImgAspect}>
                <SelectTrigger className="mt-1 h-10" data-testid="image-aspect"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="square">Square 1:1 (IG, LinkedIn)</SelectItem>
                  <SelectItem value="vertical">Vertical 9:16 (Reels, TikTok)</SelectItem>
                  <SelectItem value="landscape">Landscape 16:9 (YouTube, X)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <button onClick={generateImage} disabled={imgLoading || !imgPrompt.trim()}
              className="btn-primary mt-6 w-full justify-center" data-testid="image-generate-btn">
              {imgLoading ? <><Loader2 size={16} className="animate-spin" /> Painting…</> : <><ImageIcon size={16} /> Generate image</>}
            </button>

            {image && (
              <div className="mt-6" data-testid="image-result">
                <AuthedImage url={image.url} alt="Generated" />
                <button onClick={async () => {
                  const r = await api.get(image.url, { responseType: "blob" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(r.data);
                  a.download = image.url.split("/").pop();
                  a.click();
                  URL.revokeObjectURL(a.href);
                }} className="btn-ghost mt-3 text-sm w-full justify-center inline-flex" data-testid="image-download">
                  <Download size={14} /> Download PNG
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
